import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Spinner } from "@xola/ui-kit";
import { AgGridReact } from "ag-grid-react";
import { useMount, useUnmount } from "ahooks";
import React, { useEffect, useRef, useState } from "react";
import { Event, ExperiencePurchaseItem, Timeslot } from "types";
import { DashboardTableGroup, expandSummaryRowsAndAddFlex } from "./DashboardTable/DashboardTableGroup";
import { createMainMenuItems, onColumnPinned } from "./DashboardTable/gridUtils";
import { TableHeader } from "./DashboardTable/TableHeader";
import { useDashboardTableData } from "./DashboardTable/useDashboardTableData";
import { useDashboardTableStore } from "./DashboardTable/useDashboardTableStore";
import { usePrintViewSync } from "./DashboardTable/usePrintViewSync";
import { DashboardTablePrintWrapper } from "./DashboardTablePrintWrapper";
import { useDashboardFilterStore } from "@/stores/dashboardFilterStore";

export interface TimeslotWithMeta {
    timeslot: Timeslot;
    purchaseItem: ExperiencePurchaseItem;
    event?: Event;
}

export const DashboardTable = React.memo(() => {
    const gridReferences = useRef<Record<string, AgGridReact>>({}); // Multiple grid instances
    const tableContainerReferences = useRef<Record<string, HTMLDivElement | null>>({});
    const [hasGridRef, setHasGridRef] = useState(false);
    const [localExpandMap, setLocalExpandMap] = useState<Record<string, boolean>>({});

    const expandAllRows = useDashboardFilterStore((state) => state.displayOptions.expandAllRows);
    const columnState = useDashboardTableStore((state) => state.columnState);
    const showSummaryRows = useDashboardFilterStore((state) => state.displayOptions.showSummaryRows);

    const { timeslots, groupedData, hasNoData, experiences, isPurchaseItemsLoading, tableColumnDefs } =
        useDashboardTableData();

    usePrintViewSync(groupedData, gridReferences, tableColumnDefs);

    // Expand/Collapse all tables. Handles the logic to expand or collapse all tables simultaneously.
    useEffect(() => {
        const entries = Object.entries(gridReferences.current);
        const nextMap: Record<string, boolean> = {};

        for (const [label, ref] of entries) {
            if (!ref) continue;
            if (expandAllRows) {
                ref.api?.expandAll();
            } else {
                ref.api?.collapseAll();
            }
            nextMap[label] = expandAllRows;
            if (expandAllRows) {
                expandSummaryRowsAndAddFlex(ref.api);
            }
        }

        setLocalExpandMap(nextMap);
    }, [expandAllRows]);

    // Sync table customizations across multiple tables.
    // Ensures that table customizations stay synchronized when working with multiple tables.
    useEffect(() => {
        if (!columnState) return;
        for (const ref of Object.values(gridReferences.current)) {
            ref?.api?.applyColumnState({
                state: columnState,
                applyOrder: true,
            });
        }
    }, [columnState]);

    useMount(() => {
        document.body.style.overflow = "hidden";
    });

    // Clean up unused grids when groupedData changes (prevents memory leaks during scrolling/filtering)
    useEffect(() => {
        const currentGroupLabels = new Set(groupedData.map((group) => group.groupLabel));
        const existingGridLabels = Object.keys(gridReferences.current);

        const gridsToRemove = existingGridLabels.filter((label) => !currentGroupLabels.has(label));

        // Only proceed if there are actually grids to remove
        if (gridsToRemove.length === 0) return;

        // Cleanup unused grids with delay to avoid interrupting active operations
        const cleanupTimeout = setTimeout(() => {
            for (const label of gridsToRemove) {
                const gridToDestroy = gridReferences.current[label];
                if (gridToDestroy?.api) {
                    try {
                        gridToDestroy.api.destroy();
                    } catch (error) {
                        console.log(`Error destroying unused grid ${label}:`, error);
                    }
                }
                gridReferences.current = Object.fromEntries(
                    Object.entries(gridReferences.current).filter(([key]) => key !== label),
                );
            }
            setHasGridRef(false);
        }, 500);

        return () => clearTimeout(cleanupTimeout);
    }, [groupedData]);

    // Cleanup on leaving the Table View page. Clears the column configuration state and print view data when navigating away from the Table View.
    useUnmount(() => cleanupGridReferences());

    const cleanupGridReferences = () => {
        useDashboardTableStore.getState().actions.resetColumnState();
        useDashboardTableStore.getState().actions.resetPrintData();

        // Destroy all grids in gridReferences
        for (const gridInstance of Object.values(gridReferences.current)) {
            if (gridInstance?.api) {
                try {
                    gridInstance.api.destroy();
                } catch (error) {
                    console.log("Error destroying grid instance:", error);
                }
            }
        }
        gridReferences.current = {};
        tableContainerReferences.current = {};
        setHasGridRef(false);
        document.body.style.overflow = "";
    };

    if (hasNoData) {
        return (
            <div className="p-4 text-center">
                {isPurchaseItemsLoading ? (
                    <Spinner />
                ) : (
                    <div className="flex flex-col justify-center">
                        {hasNoData && <span className="pb-4 pt-4 text-lg font-bold text-black">No results found</span>}
                    </div>
                )}
            </div>
        );
    }

    return (
        <>
            {hasGridRef && (
                <TableHeader
                    timeslots={timeslots}
                    experiences={experiences}
                    tableRef={{ current: Object.values(gridReferences.current)[0] || null }}
                />
            )}
            <div className="h-full overflow-y-auto">
                {groupedData.map((group) => {
                    if (groupedData.length > 1 && group.rowData.length === (showSummaryRows ? 1 : 0)) {
                        return null;
                    }

                    return (
                        <DashboardTableGroup
                            ref={(ref) => {
                                if (ref) {
                                    gridReferences.current[group.groupLabel] = ref;
                                    if (!hasGridRef) {
                                        setHasGridRef(true);
                                    }
                                }
                            }}
                            key={group.groupLabel}
                            columnDefs={tableColumnDefs}
                            group={group}
                            tableContainerReferences={tableContainerReferences}
                            localExpandMap={localExpandMap}
                            setLocalExpandMap={setLocalExpandMap}
                            getMainMenuItems={createMainMenuItems(group.groupLabel, gridReferences.current)}
                            onColumnPinned={() => {
                                return onColumnPinned(gridReferences.current[group.groupLabel]?.api, 3);
                            }}
                        />
                    );
                })}
            </div>
            <DashboardTablePrintWrapper />
        </>
    );
});

DashboardTable.displayName = "DashboardTable";
